import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Paper, Container, CircularProgress, Alert, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Grid, Pagination,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

const AnswerTable = ({ title, answers, action, actionLabel, cancelAction, timeLabel, icon }) => (
  <Grid item xs={12}>
    <Paper sx={{ p: 3, borderRadius: '12px', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.07)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon}
        <Typography variant="h6" component="h2" sx={{ ml: 1, fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {answers.length > 0 ? (
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>유형</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>평가 종류</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>연도/월</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>과목/세트</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{timeLabel}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>동작</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {answers.map((answer) => {
                let yearMonth = 'N/A';
                if (answer.type === 'MEE' && answer.mee_paper_sets && answer.mee_paper_sets.mee_year_month_sets) {
                  const { year, month } = answer.mee_paper_sets.mee_year_month_sets;
                  yearMonth = `${year} ${month}`;
                } else if (answer.type === 'MPT' && answer.mpt_paper_sets && answer.mpt_paper_sets.mpt_year_month_sets) {
                  const { year, month } = answer.mpt_paper_sets.mpt_year_month_sets;
                  yearMonth = `${year} ${month}`;
                }
                
                return (
                  <TableRow
                    key={`${answer.type}-${answer.id}`}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <TableCell component="th" scope="row">
                      <Chip 
                        label={answer.type} 
                        color={answer.type === 'MEE' ? 'primary' : 'secondary'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={answer.evaluation_type === 'ai' ? 'AI' : 'Professor'}
                        color={answer.evaluation_type === 'ai' ? 'info' : 'success'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{yearMonth}</TableCell>
                    <TableCell>{answer.subject || '과목 없음'}</TableCell>
                    <TableCell>
                      {new Date(answer.claimed_at || answer.created_at).toLocaleString('ko-KR')}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button 
                          variant="contained" 
                          onClick={() => action(answer.id, answer.type)}
                          sx={{
                            bgcolor: actionLabel === '평가 계속하기' ? '#1976d2' : '#4caf50',
                            '&:hover': {
                              bgcolor: actionLabel === '평가 계속하기' ? '#1565c0' : '#388e3c',
                            }
                          }}
                        >
                          {actionLabel}
                        </Button>
                        {cancelAction && (
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => cancelAction(answer.id, answer.type)}
                          >
                            평가 취소
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'grey.50', borderRadius: '8px' }}>
          <PlaylistAddCheckIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
          <Typography color="text.secondary">
            해당하는 답안이 없습니다.
          </Typography>
        </Box>
      )}
    </Paper>
  </Grid>
);

const EvaluatorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingAnswers, setPendingAnswers] = useState([]);
  const [claimedAnswers, setClaimedAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingPage, setPendingPage] = useState(1);

  const PENDING_PAGE_SIZE = 5; // 한 페이지에 5개씩 표시

  const fetchAnswers = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const { data: pendingMee, error: meeError } = await supabase
        .from('mee_answers')
        .select('id, question_id, created_at, user_id, evaluation_status, subject, evaluation_type, mee_paper_sets!fk_mee_paper_sets(mee_year_month_set_id, mee_year_month_sets(year, month))')
        .eq('evaluation_status', '대기중')
        .or('evaluation_type.neq.self,evaluation_type.is.null');
      if (meeError) throw meeError;

      const { data: pendingMpt, error: mptError } = await supabase
        .from('mpt_answers')
        .select('id, created_at, user_id, evaluation_status, set_title, evaluation_type, mpt_paper_sets!question_id(mpt_year_month_set_id, mpt_year_month_sets(year, month))')
        .eq('evaluation_status', '대기중')
        .or('evaluation_type.neq.self,evaluation_type.is.null');
      if (mptError) throw mptError;
      
      setPendingAnswers([
        ...pendingMee.map(a => ({ ...a, type: 'MEE' })),
        ...pendingMpt.map(a => ({ ...a, type: 'MPT', subject: a.set_title }))
      ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));

      const { data: claimedMee, error: claimedMeeError } = await supabase
        .from('mee_answers')
        .select('id, question_id, created_at, user_id, evaluation_status, claimed_at, subject, evaluation_type, mee_paper_sets!fk_mee_paper_sets(mee_year_month_set_id, mee_year_month_sets(year, month))')
        .eq('evaluator_id', user.id)
        .eq('evaluation_status', '진행중');
      if (claimedMeeError) throw claimedMeeError;

      const { data: claimedMpt, error: claimedMptError } = await supabase
        .from('mpt_answers')
        .select('id, created_at, user_id, evaluation_status, claimed_at, set_title, evaluation_type, mpt_paper_sets!question_id(mpt_year_month_set_id, mpt_year_month_sets(year, month))')
        .eq('evaluator_id', user.id)
        .eq('evaluation_status', '진행중');
      if (claimedMptError) throw claimedMptError;

      setClaimedAnswers([
        ...claimedMee.map(a => ({ ...a, type: 'MEE' })),
        ...claimedMpt.map(a => ({ ...a, type: 'MPT', subject: a.set_title }))
      ].sort((a, b) => new Date(b.claimed_at) - new Date(a.claimed_at)));

    } catch (err) {
      console.error("답안 로딩 상세 오류:", err);
      setError(`답안 목록 로딩 오류: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAnswers();
  }, [fetchAnswers]);

  const handleClaim = async (answerId, type) => {
    try {
      const tableName = type === 'MEE' ? 'mee_answers' : 'mpt_answers';
      const { error } = await supabase
        .from(tableName)
        .update({
          evaluator_id: user.id,
          claimed_at: new Date().toISOString(),
          evaluation_status: '진행중',
        })
        .eq('id', answerId);

      if (error) throw error;
      
      handleNavigate(answerId, type);

    } catch (err) {
      console.error("답안 선점 오류:", err);
      alert(`답안 선점 오류: ${err.message}`);
    }
  };
  
  const handleCancel = async (answerId, type) => {
    if (window.confirm('정말로 이 평가를 취소하시겠습니까? 다른 평가자가 평가를 진행할 수 있게 됩니다.')) {
      try {
        const rpcName = type === 'MEE' ? 'cancel_mee_evaluation' : 'cancel_mpt_evaluation';
        const { error } = await supabase.rpc(rpcName, {
          answer_id: answerId
        });

        if (error) throw error;
        
        fetchAnswers();

      } catch (err) {
        console.error("평가 취소 오류:", err);
        alert(`평가 취소 중 오류가 발생했습니다: ${err.message}`);
      }
    }
  };

  const handleNavigate = (answerId, type) => {
    navigate(`/evaluator/evaluate/${type.toLowerCase()}/${answerId}`);
  };

  if (loading) {
    return <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Container>;
  }

  if (error) {
    return <Container maxWidth="lg" sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  const pagedPendingAnswers = pendingAnswers.slice(
    (pendingPage - 1) * PENDING_PAGE_SIZE,
    pendingPage * PENDING_PAGE_SIZE
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 4,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderRadius: '12px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WorkspacePremiumIcon sx={{ fontSize: { xs: 32, md: 48 }, mr: 2, opacity: 0.8 }} />
          <Box>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                평가자 대시보드
              </Typography>
              <Typography variant="body1">
                이곳에서 MEE 및 MPT 답안을 평가하고 관리할 수 있습니다.
              </Typography>
          </Box>
        </Box>
      </Paper>
      
      <Grid container spacing={4}>
        <AnswerTable 
          title="내가 진행중인 평가"
          answers={claimedAnswers}
          action={handleNavigate}
          actionLabel="평가 계속하기"
          cancelAction={handleCancel}
          timeLabel="선점 시간"
          icon={<RateReviewIcon color="primary" />}
        />
        <Grid item xs={12}>
          <AnswerTable 
            title="평가 대기중인 답안"
            answers={pagedPendingAnswers}
            action={handleClaim}
            actionLabel="평가 시작하기"
            timeLabel="제출 시간"
            icon={<HourglassTopIcon color="secondary" />}
          />
          {pendingAnswers.length > PENDING_PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, p: 2 }}>
              <Pagination
                count={Math.ceil(pendingAnswers.length / PENDING_PAGE_SIZE)}
                page={pendingPage}
                onChange={(e, value) => setPendingPage(value)}
                color="primary"
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default EvaluatorDashboard; 